import wrapper from 'wrapper';
import highlight from '../../../../bin/helpers/handlebars/highlight';
import markdownToHTML from '../../../../bin/helpers/handlebars/markdown-to-html';
import { sectionColors } from '../../shared_variables';

export default class ResultsHandler {
    constructor() {
        this.resultsContainer = wrapper.select('.my-lookup__results');
        this.sections = [
            __('amb__section-id'),
            __('ae__section-id'),
            __('ops__section-id'),
        ];
        this.stateInitialized = false;
    }

    init(data, n) {
        this.data = data;
        this.nation = n === 'Northern Ireland' ? 'ni' : n.toLowerCase();
        this.infoBox = wrapper.select('.info-box');
        this.infoBtn = wrapper.select('.info-button');
        if (!this.stateInitialized) this.initState(); // Set button state if first call
        this.resetEls();
        this.setProviderName();
        this.setInfoBox();
        this.setVocabsObj();
        this.populateSections();
        this.populateData();
        this.populateMethodology();
        this.showContainers();
        wrapper.select('.news-vj-search-lookup__input').blur();
    }

    setVocabsObj() {
        const boardOrTrust = this.nation === 'england' || this.nation === 'ni'
            ? 'trust'
            : 'board';
        this.vocabs = {};

        this.sections.forEach((section) => {
            this.vocabs[section] = {
                section__title: `${__(`${section}__title_${this.nation}`)}`,
                section__para: `${__(`${section}__para_${this.nation}`)}`,
                current_label: `${__(`label__${boardOrTrust}_current`)}`,
                current_total: `${__(`${section}__current_total_${this.nation}`)}`,
                nation_label: `${__('label__nation-average')}`,
                nation_value: `${__(`${section}__nation-average_value`)}`,
                past_label: `${__(`label__${boardOrTrust}_past`)}`.replace(
                    'section',
                    `${section}`,
                ),
                past_total: `${__(`${section}__past_total_${this.nation}`)}`,
            };
        });
    }

    showContainers() {
        wrapper
            .select('.my-lookup__results-container')
            .classList.remove('hidden');
        wrapper.select('.last-updated').classList.remove('hidden');
        wrapper.select('.methodology__container').classList.remove('hidden');
    }

    setProviderName() {
        const selectedProvider = wrapper.select('.results__provider_name');
        selectedProvider.innerText = this.data.name;
    }

    setInfoBox() {
        const infoBtn = wrapper.select('.info-button');
        const infoBtnText = wrapper.select('.info-box__text');
        this.infoBox.classList.add(`info-box__${this.nation}`);
        infoBtn.setAttribute(
            'aria-label',
            __(`info-box__aria-label_${this.nation}`),
        );
        infoBtn.dataset.id = `info-box__${this.nation}`;
        infoBtnText.innerHTML = markdownToHTML(__(`info-box__text_${this.nation}`));

        const infoBoxLink = wrapper.select('.info-box__text a');
        if (infoBoxLink) infoBoxLink.classList.add('info-box__link');
    }

    setListeners() {
        const hideInfoPanel = (e) => {
            const isNotTextOrLink = !((e.target.classList.contains('info-box__text')) || (e.target.classList.contains('info-box__link')));
            if (isNotTextOrLink) {
                if (this.infoButtonState === 'open') {
                    e.preventDefault();
                    this.updateInfoButtonState();
                    this.updateVisibility();
                }
            }
        };

        this.infoBox.removeEventListener('blur', hideInfoPanel);
        wrapper.scope.removeEventListener('click', hideInfoPanel);

        this.infoBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const state = this.infoButtonState;
            hideInfoPanel(e);

            if (state !== 'open' && this.stateInitialized) {
                this.updateInfoButtonState();
                this.updateVisibility();

                wrapper.scope.addEventListener('click', hideInfoPanel);
            }
        });

        this.infoBox.addEventListener('blur', hideInfoPanel);
    }

    updateInfoButtonState() {
        const state = this.infoButtonState === 'closed' ? 'open' : 'closed';
        this.infoButtonState = state;
    }

    updateVisibility() {
        // eslint-disable-next-line no-unused-expressions
        this.infoButtonState === 'closed' ?
            this.infoBox.classList.remove('info-box--open') :
            this.infoBox.classList.add('info-box--open');
    }

    initState() {
        this.infoButtonState = 'closed';
        this.stateInitialized = true;
        this.setListeners();
    }

    populateSections() {
        this.sections.forEach((section) => {
            const vocabs = this.vocabs[section];
            Object.entries(vocabs).forEach(([key, value]) => {
                const el = wrapper.select(`.${section} .${key}`);
                el.innerHTML = highlight(value);
            });
        });

        // search EH2 or BT1 to check text is displayed instead of progress bar for Ambulance responses in Scotland / NI
        if (this.nation === 'scotland' || this.nation === 'ni') {
            wrapper.select('.current_progress-bar').classList.add('hidden');
        }
    }

    populateData() {
        this.sections.forEach((section) => {
            const vocabs = {
                ...this.vocabs[section],
            };
            Object.keys(vocabs).forEach((key) => {
                const el = wrapper.select(`.${section} .${key}`);
                const text = el.innerHTML;
                const regex = /\{{(.*?)\}}/g; // find anything between double curly braces

                let match;
                while ((match = regex.exec(text)) !== null) {
                    let value = this.data[match[1]];
                    const isNumber = /^\d+$/.test(value); // matches if value contains only numbers

                    if (value === 'NA') {
                        this.handleMissingData(key, section, match[1]);
                    } else {
                        if (isNumber && !key.includes('label')) {
                            value = parseFloat(value).toLocaleString('en');
                        }
                        el.innerHTML = el.innerHTML.replace(match[0], value);

                        if (
                            (this.nation === 'scotland' &&
                                section === 'amb' &&
                                key === 'nation_value') ||
                            (this.nation === 'ni' &&
                                section === 'amb' &&
                                key === 'nation_value')
                        ) {
                            el.innerText = el.innerText.replace('%', '');
                        }
                    }
                }
            });
            this.populateProgressBar(section);
        });
    }

    populateProgressBar(section) {
        const currentValue = this.data[`${section}_current_value`];
        if (currentValue === 'NA') {
            this.handleMissingData('current_total', section, `${section}_current_value`);
        }
        else {
            const progressBarWidth = Number(this.data[`${section}_current_value`]);
            const percentageBar = wrapper.select(`.${section} .current_progress-bar .percentage-bar`);
            percentageBar.style.width = `${progressBarWidth}%`;

            const percentageFigureEl = wrapper.select(`.${section} .current_progress-bar .percentage-figure`);
            percentageFigureEl.innerText = `${progressBarWidth}%`;

            this.positionProgressBarValue(progressBarWidth, section);
            this.populateProgressBarOffscreenEl(section, progressBarWidth);
        }
    }

    positionProgressBarValue(progressBarWidth, section) {
        const percentageFigureEl = wrapper.select(`.${section} .current_progress-bar .percentage-figure`);

        if (progressBarWidth < 20) {
            percentageFigureEl.style.marginLeft = '0.5rem';
            percentageFigureEl.style.left = '100%';
            percentageFigureEl.style.color = `${sectionColors[section]}`;
        } else {
            percentageFigureEl.style.marginRight = '0.5rem';
            percentageFigureEl.style.right = '0';
            percentageFigureEl.style.left = 'unset';
            percentageFigureEl.style.color = 'white';
        }
    }

    populateProgressBarOffscreenEl(section, progressBarWidth) {
        const currentTotal = wrapper.select(`.${section} .current_total`).innerText;
        const offscreenEl = wrapper.select(`.${section} .current_progress-bar_offscreen`);
        if (Number(progressBarWidth)) offscreenEl.innerText = `${progressBarWidth}% ${currentTotal}`;
        else offscreenEl.innerText = `${currentTotal}`;
    }

    populateMethodology() {
        const methodologyContent = wrapper.select('.methodology__contents');
        const contentContainer = wrapper.select('.methodology__sections');
        contentContainer.innerHTML = '';

        if (methodologyContent.classList.contains('expanded')) {
            methodologyContent.classList.remove('expanded');
            wrapper.select('.methodology--button').classList.remove('expanded');
        }

        this.sections.forEach((section, i) => {
            contentContainer.innerHTML += `
                <h3 class="methodology__subtitle subtitle-${i + 1}">${__(`${section}__title_${this.nation}`)}</h3>
                <p class="methodology__para">${__(`methodology__${section}_${this.nation}`)}</p>
            `;
        });
    }

    handleMissingData(className, section, dataKey) {
        if (dataKey.includes('current')) {
            const currentTotalEl = wrapper.select(`.${section} .${className}`);
            currentTotalEl.innerHTML = __('no-data-msg');
            currentTotalEl.removeAttribute('aria-hidden');

            wrapper
                .select(`.${section} .current_progress-bar`)
                .classList.add('hidden');
            wrapper
                .select(`.${section} .section__past`)
                .classList.add('hidden');
            wrapper.select(`.${section} .current_progress-bar_offscreen`).classList.add('hidden');
        } else {
            wrapper.select(`.${section} .past_label`).innerText = __(
                'label__no-data_past',
            );
            wrapper.select(`.${section} .past_total`).innerText = __('no-data-msg');
        }
    }

    resetEls() {
        this.sections.forEach((section) => {
            wrapper
                .select(`.${section} .current_progress-bar`)
                .classList.remove('hidden');
            wrapper
                .select(`.${section} .section__past`)
                .classList.remove('hidden');
            wrapper.select('.current_progress-bar').classList.remove('hidden');
            wrapper.select(`.${section} .current_total`).setAttribute('aria-hidden', 'true');
            wrapper.select(`.${section} .current_progress-bar_offscreen`).classList.remove('hidden');
            wrapper.select(`.${section} .current_progress-bar_offscreen`).innerText = '';
        });
    }
}
